import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { useToast } from "../../context/toastContext";
import apiClient from "../../api/apiClient";
import Button from "../ui/Button";
import "./ReceiptUpload.css";

const MAX_FILES = 5;
const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

const ACCEPT = {
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
    "application/pdf": [".pdf"],
};

const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ReceiptUpload = ({ orderId, licenseNo, onUploadComplete }) => {
    const toast = useToast();
    const [files, setFiles] = useState([]);
    const [signedCoc, setSignedCoc] = useState(null);
    const [isUploading, setIsUploading] = useState(false);

    const onDrop = useCallback(
        (acceptedFiles, rejectedFiles) => {
            if (rejectedFiles.length > 0) {
                const reasons = rejectedFiles
                    .flatMap((r) => r.errors)
                    .map((e) => e.message);
                const unique = [...new Set(reasons)];
                toast.warning(unique.join(". "));
            }

            setFiles((prev) => {
                const combined = [...prev, ...acceptedFiles];
                if (combined.length > MAX_FILES) {
                    toast.warning(`Maximum ${MAX_FILES} files allowed.`);
                    return combined.slice(0, MAX_FILES);
                }
                return combined;
            });
        },
        [toast]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: ACCEPT,
        maxSize: MAX_SIZE_BYTES,
        maxFiles: MAX_FILES - files.length,
        disabled: isUploading,
    });

    const removeFile = useCallback((index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const handleCocFileChange = useCallback((e) => {
        const file = e.target.files?.[0] || null;
        if (file && file.size > MAX_SIZE_BYTES) {
            toast.warning("The signed document must be 10 MB or smaller.");
            return;
        }
        setSignedCoc(file);
    }, [toast]);

    const canSubmit = files.length > 0 && Boolean(signedCoc) && !isUploading;

    const handleUpload = async () => {
        if (!canSubmit) return;

        setIsUploading(true);

        try {
            const formData = new FormData();
            formData.append("licenseNo", licenseNo);
            files.forEach((file) => {
                formData.append("photos", file);
            });
            formData.append("signed_coc", signedCoc);

            await apiClient.post(`/orders/${orderId}/receive`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            toast.success("Receipt confirmed successfully");
            setFiles([]);
            setSignedCoc(null);
            if (onUploadComplete) onUploadComplete();
        } catch (err) {
            const msg =
                err.response?.data?.error ||
                err.userMessage ||
                "Failed to upload photos. Please try again.";
            toast.error(msg);
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className="receipt-upload">
            <div
                {...getRootProps()}
                className={`receipt-upload__dropzone ${isDragActive ? "receipt-upload__dropzone--active" : ""} ${isUploading ? "receipt-upload__dropzone--disabled" : ""}`}
            >
                <input {...getInputProps()} />
                <div className="receipt-upload__dropzone-content">
                    <svg
                        className="receipt-upload__icon"
                        width="32"
                        height="32"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    {isDragActive ? (
                        <p className="receipt-upload__text">Drop files here</p>
                    ) : (
                        <>
                            <p className="receipt-upload__text">
                                Drag & drop packaging photos here, or click to browse
                            </p>
                            <p className="receipt-upload__hint">
                                JPG, PNG, or PDF — max {MAX_FILES} files, 10 MB each
                            </p>
                        </>
                    )}
                </div>
            </div>

            {files.length > 0 && (
                <div className="receipt-upload__previews">
                    {files.map((file, idx) => {
                        const isImage = file.type.startsWith("image/");
                        const preview = isImage ? URL.createObjectURL(file) : null;

                        return (
                            <div key={`${file.name}-${idx}`} className="receipt-upload__preview">
                                <div className="receipt-upload__preview-thumb">
                                    {isImage ? (
                                        <img
                                            src={preview}
                                            alt={file.name}
                                            className="receipt-upload__preview-img"
                                            onLoad={() => {
                                                if (preview) URL.revokeObjectURL(preview);
                                            }}
                                        />
                                    ) : (
                                        <div className="receipt-upload__preview-pdf">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
                                                <path d="M14 2v6h6" />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                                <div className="receipt-upload__preview-info">
                                    <span className="receipt-upload__preview-name">{file.name}</span>
                                    <span className="receipt-upload__preview-size">
                                        {formatSize(file.size)}
                                    </span>
                                </div>
                                <button
                                    className="receipt-upload__preview-remove"
                                    onClick={() => removeFile(idx)}
                                    type="button"
                                    aria-label={`Remove ${file.name}`}
                                    disabled={isUploading}
                                >
                                    &times;
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="receipt-upload__coc">
                <label className="receipt-upload__coc-label" htmlFor="receipt-upload-coc-input">
                    Signed Chain of Custody document
                </label>
                <p className="receipt-upload__hint">
                    Upload a photo or scan of the fully signed Chain of Custody form (both parties'
                    signatures visible).
                </p>
                {signedCoc ? (
                    <div className="receipt-upload__preview">
                        <div className="receipt-upload__preview-thumb">
                            {signedCoc.type.startsWith("image/") ? (
                                <img
                                    src={URL.createObjectURL(signedCoc)}
                                    alt={signedCoc.name}
                                    className="receipt-upload__preview-img"
                                />
                            ) : (
                                <div className="receipt-upload__preview-pdf">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
                                        <path d="M14 2v6h6" />
                                    </svg>
                                </div>
                            )}
                        </div>
                        <div className="receipt-upload__preview-info">
                            <span className="receipt-upload__preview-name">{signedCoc.name}</span>
                            <span className="receipt-upload__preview-size">{formatSize(signedCoc.size)}</span>
                        </div>
                        <button
                            className="receipt-upload__preview-remove"
                            onClick={() => setSignedCoc(null)}
                            type="button"
                            aria-label="Remove signed Chain of Custody document"
                            disabled={isUploading}
                        >
                            &times;
                        </button>
                    </div>
                ) : (
                    <input
                        id="receipt-upload-coc-input"
                        type="file"
                        accept="image/jpeg,image/png,application/pdf"
                        onChange={handleCocFileChange}
                        disabled={isUploading}
                    />
                )}
            </div>

            {(files.length > 0 || signedCoc) && (
                <div className="receipt-upload__actions">
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleUpload}
                        disabled={!canSubmit}
                        loading={isUploading}
                    >
                        {isUploading ? "Uploading..." : "Confirm Receipt"}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default ReceiptUpload;
